import React from 'react';
import LazyLoadImage from '@components/shared/LazyLoadImage';
import { DID_YOU_KNOW_BULB_SVG, GOLD_ALERT_ICON, HEART_ICON, INFO_ICON, NURSE_ICON, ORDER_ICON, PURPLE_ALERT_ICON } from '@constants';
import IconDayeMonogram from '@icons/daye-monogram-one-color.svg';
import IconHelp from '@icons/help.svg';
import Hourglass from '@images/vms-account/vms-icons/Hourglass.svg';

/**
 * Renders an icon component based on the provided icon type.
 * Can be extended to cover other icon types by name with provided src for image.
 *
 * @param icon - Can be either a React node for custom icons, or one of the predefined icon types:
 *              'greeting', 'did-you-know', 'info', 'warning', 'error', 'hourglass', 'nurse', 'order'
 * @returns A React component representing the icon, or null if no valid icon is provided
 *
 * @example
 * // Using a predefined icon type
 * renderIcon('warning') // Returns LazyLoadImage with GOLD_ALERT_ICON
 *
 * // Using a custom React node
 * renderIcon(<CustomIcon />) // Returns the CustomIcon component directly
 *
 * // Invalid or no input
 * renderIcon(null) // Returns null
 */
export const renderIcon = (icon: React.ReactNode | 'greeting' | 'did-you-know' | 'info' | 'warning' | 'error' | 'hourglass' | 'nurse' | 'order') => {
  if (icon) {
    if (typeof icon !== 'string') {
      return icon;
    }
    switch (icon) {
      case 'daye-monogram':
        return <IconDayeMonogram />;
      case 'help':
        return <IconHelp />;
      case 'greeting':
        return <LazyLoadImage src={HEART_ICON} />;
      case 'did-you-know':
        return <LazyLoadImage src={DID_YOU_KNOW_BULB_SVG} />;
      case 'info':
        return <LazyLoadImage src={INFO_ICON} />;
      case 'warning':
        return <LazyLoadImage src={GOLD_ALERT_ICON} />;
      case 'error':
        return <LazyLoadImage src={PURPLE_ALERT_ICON} />;
      case 'nurse':
        return <LazyLoadImage src={NURSE_ICON} />;
      case 'order':
        return <LazyLoadImage src={ORDER_ICON} />;
      case 'hourglass':
        return <Hourglass />;
      default:
        return null;
    }
  }
  return null;
};
